import { ref, type App, type Component, createApp } from 'vue';

import AppLayout from './components/AppLayout.vue';
import FreyaApp from './FreyaApp.vue';
import AppTopbarItem from './components/AppTopbarItem.vue';
import ColorSchemeSelect from './components/ColorSchemeSelect.vue';
import MenuModeSelect from './components/MenuModeSelect.vue';
import { defaultHomeRouteInjectionKey } from './symbols';
import './shims';
import {
    useAppConfigStore,
    type AppConfigOptions,
} from './stores/app-config-store';
import { useAppMenuItemsStore } from './stores/app-menu-items';
import { createPinia, getActivePinia, type Pinia } from 'pinia';

import ConfirmationService from 'primevue/confirmationservice';
import DialogService from 'primevue/dialogservice';
import ToastService from 'primevue/toastservice';

import PrimeVue from 'primevue/config';
import StyleClass from 'primevue/styleclass';
import Tooltip from 'primevue/tooltip';
import Ripple from 'primevue/ripple';
import BadgeDirective from 'primevue/badgedirective';

import './styles.scss';
import type { MenuItem } from 'primevue/menuitem';

import { createI18n } from 'vue-i18n';
import messages from '@intlify/unplugin-vue-i18n/messages';
import {
    useColorSchemeStylesheetStore,
    type StylesheetReferences,
} from '@precon-tools-suite-by-shared/vue-color-schemes';

import {
    type CreatePrettyLoggerOptions,
    vuePrettyConsole,
} from '@precon-tools-suite-by-shared/pretty-console';

import type { RouteLocationRaw } from 'vue-router';
export type { RouteLayout } from './router';

import { addAppRightBarPartials } from './stores/app-right-panel';
export {
    AppLayout,
    AppTopbarItem,
    ColorSchemeSelect,
    MenuModeSelect,
    addAppRightBarPartials,
};

export interface FreyaLayoutOptions extends AppConfigOptions {
    prettyLogger?: CreatePrettyLoggerOptions | boolean;
    defaultHomeRoute?: RouteLocationRaw;
    pinia?: Pinia | null | undefined;
    menuItems?: MenuItem[];
    theme?: StylesheetReferences;
    userProfileComponent?: Component;
    rightBarPartials?: string[];
}

export const i18n = createI18n({
    legacy: false,
    locale: 'nl',
    fallbackLocale: 'en',
    messages,
});

const plugin = {
    install(app: App, options: FreyaLayoutOptions = {}) {
        if (options.prettyLogger !== false) {
            app.use(
                vuePrettyConsole,
                options.prettyLogger === true ? undefined : options.prettyLogger
            );
        }

        const pinia = options.pinia ?? getActivePinia() ?? createPinia();
        app.use(pinia);
        app.use(i18n);

        if (options.theme) {
            const stylesheetStore = useColorSchemeStylesheetStore(pinia);
            stylesheetStore.set('app-layout-theme-styles', options.theme);
        }

        app.config.globalProperties.$layout = {
            userProfileComponent: null,
        };

        if (options.rightBarPartials)
            addAppRightBarPartials(...options.rightBarPartials);

        app.use(PrimeVue, {
            ripple: true,
        });

        app.use(ConfirmationService);
        app.use(DialogService);
        app.use(ToastService);

        const configStore = useAppConfigStore(pinia);
        configStore.primeVue = app.config.globalProperties.$primevue;
        configStore.configure(options);

        if (options.menuItems) {
            const menuItemsStore = useAppMenuItemsStore(pinia);
            menuItemsStore.items = options.menuItems;
        }

        const defaultHomeRoute = ref(options.defaultHomeRoute ?? '/');
        app.provide(defaultHomeRouteInjectionKey, defaultHomeRoute);

        app.directive('tooltip', Tooltip);
        app.directive('ripple', Ripple);
        app.directive('badge', BadgeDirective);
        app.directive('styleclass', StyleClass);
        app.component('AppLayout', AppLayout);
    },
};

export function createFreyaApp(options: FreyaLayoutOptions = {}): App {
    const app = createApp(FreyaApp);
    app.use(plugin, options);
    return app;
}

export default Object.assign(FreyaApp, plugin);
